/**
 * WebSocket connection state and close codes for Aegix One.
 *
 * Reconnection behavior follows ADR-049 (server-initiated drain, token
 * refresh, and auth-failure close codes). Close codes are defined in the
 * RFC 6455 private-use range (4000-4999).
 */

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'auth_failed';

export interface ConnectionState {
  status: ConnectionStatus;
  lastConnected: Date | null;
  reconnectAttempt: number;
}

export const WS_CLOSE_CODES = {
  PONG_TIMEOUT: 4000,
  TOKEN_REFRESH: 4001,
  AUTH_FAILED: 4002,
  SERVER_SHUTDOWN: 4010,
} as const;

export const GRACEFUL_CLOSE_CODES = new Set<number>([
  WS_CLOSE_CODES.SERVER_SHUTDOWN,
  WS_CLOSE_CODES.TOKEN_REFRESH,
]);

export const AUTH_FAILURE_CLOSE_CODES = new Set<number>([
  WS_CLOSE_CODES.AUTH_FAILED,
]);

export function getConnectionMessage(state: ConnectionState): string {
  switch (state.status) {
    case 'idle':
      return '';
    case 'connecting':
      return 'Connecting...';
    case 'connected':
      return 'Connected';
    case 'reconnecting':
      return `Reconnecting... (attempt ${state.reconnectAttempt})`;
    case 'disconnected':
      return 'Connection lost. Updates may be delayed.';
    case 'auth_failed':
      return 'Session expired. Please sign in again.';
  }
}

// Reconnection constants (ADR-049)
export const BASE_DELAY_MS = 1000;
export const MAX_DELAY_MS = 30_000;
export const JITTER_FACTOR = 0.2;
export const HARD_MAX_RETRIES = 50;
export const GRACEFUL_RECONNECT_DELAY_MS = 500;
export const STABLE_CONNECTION_THRESHOLD_MS = 30_000;

function addJitter(delay: number): number {
  const jitter = delay * JITTER_FACTOR * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

export function getReconnectDelay(retryCount: number): number {
  const exponentialDelay = Math.min(
    BASE_DELAY_MS * Math.pow(2, retryCount),
    MAX_DELAY_MS,
  );
  return addJitter(exponentialDelay);
}
